import React, { useRef, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import '../styles/ParticleBackground.scss';

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const { isPro } = useUser();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    let mouse = { x: -100, y: -100 };

    const resize = () => {
      // High DPI support - render at device pixel ratio for crisp graphics
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    resize();

    class Particle {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.baseX = x;
        this.baseY = y;
        this.density = (Math.random() * 30) + 1;
      }

      draw() {
        // Use darker colors based on theme - sharp and crisp
        const isDarkMode = document.body.classList.contains('dark');
        let color, alpha;

        if (isPro) {
          // Purple for PRO
          color = isDarkMode ? '#c084fc' : '#a855f7';
          alpha = isDarkMode ? 0.8 : 0.6;
        } else {
          // Orange for default - darker for light theme
          color = isDarkMode ? '#FF6B00' : '#CC5500';
          alpha = isDarkMode ? 0.8 : 0.7;
        }

        ctx.fillStyle = `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, 2, 0, Math.PI * 2); // Smaller, sharper particles
        ctx.fill();
      }

      update() {
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = 100;

        if (distance < maxDistance) {
          const force = (maxDistance - distance) / maxDistance;
          this.x -= (dx / distance) * force * this.density;
          this.y -= (dy / distance) * force * this.density;
        } else {
          // Smoothly return to base position
          this.x -= (this.x - this.baseX) / 10;
          this.y -= (this.y - this.baseY) / 10;
        }
      }
    }

    const particles = [];

    const init = () => {
      particles.length = 0;
      const spacing = 45;
      for (let y = 0; y < canvas.height; y += spacing) {
        for (let x = 0; x < canvas.width; x += spacing) {
          particles.push(new Particle(x, y));
        }
      }
    };

    init();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isPro]);

  return <canvas ref={canvasRef} className="particle-canvas" />;
};

export default ParticleBackground;